// A constrained recurrence model, deliberately not RFC 5545.
//
// Venues need four shapes: one-off, weekly, n-th weekday of the month, and a
// fixed day of the month. Supporting all of iCalendar would mean a frozen
// dependency and a UI nobody can use. We generate valid RRULE strings for
// export (see lib/ics.js); we never parse foreign ones here.
//
// Everything in this module is plain calendar arithmetic on 'YYYY-MM-DD'
// strings. No timezone is involved — that mapping happens once, later, in
// lib/timezone.js.

export const WEEKDAYS = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

const DAY_MS = 86400000;
const FREQS = new Set(['once', 'weekly', 'monthly']);

export class RecurrenceError extends Error {}

export function normaliseRule(input) {
  const rule = typeof input === 'string' ? JSON.parse(input) : { ...input };

  if (!FREQS.has(rule.freq)) {
    throw new RecurrenceError(`unknown freq: ${rule.freq}`);
  }

  const out = {
    freq: rule.freq,
    interval: clampInterval(rule.interval),
    byday: null,
    bysetpos: null,
    bymonthday: null,
    until: rule.until ?? null,
    exdates: Array.isArray(rule.exdates) ? [...new Set(rule.exdates)].sort() : [],
  };

  if (rule.until && !isDate(rule.until)) {
    throw new RecurrenceError(`until must be YYYY-MM-DD: ${rule.until}`);
  }
  for (const d of out.exdates) {
    if (!isDate(d)) throw new RecurrenceError(`exdate must be YYYY-MM-DD: ${d}`);
  }

  if (rule.freq === 'weekly') {
    out.byday = normaliseByday(rule.byday);
    if (out.byday.length === 0) {
      throw new RecurrenceError('weekly needs at least one byday');
    }
  }

  if (rule.freq === 'monthly') {
    const hasMonthday = rule.bymonthday !== null && rule.bymonthday !== undefined;
    if (hasMonthday) {
      const n = Number(rule.bymonthday);
      if (!Number.isInteger(n) || n < 1 || n > 31) {
        throw new RecurrenceError(`bymonthday out of range: ${rule.bymonthday}`);
      }
      out.bymonthday = n;
    } else {
      out.byday = normaliseByday(rule.byday);
      if (out.byday.length !== 1) {
        throw new RecurrenceError('monthly by weekday needs exactly one byday');
      }
      const pos = Number(rule.bysetpos);
      if (![1, 2, 3, 4, -1].includes(pos)) {
        throw new RecurrenceError(`bysetpos must be 1..4 or -1, got ${rule.bysetpos}`);
      }
      out.bysetpos = pos;
    }
  }

  return out;
}

// Local calendar dates the series falls on, inside [from, to].
// startDate is the anchor: intervals count from its week/month.
export function expand({ startDate, rule, from, to, limit = 5000 }) {
  if (!isDate(startDate)) throw new RecurrenceError(`bad startDate: ${startDate}`);
  if (!isDate(from) || !isDate(to)) throw new RecurrenceError('bad window');

  const r = normaliseRule(rule);
  const exdates = new Set(r.exdates);

  const windowStart = maxDate(from, startDate);
  const windowEnd = r.until ? minDate(to, r.until) : to;
  if (windowStart > windowEnd) return [];

  if (r.freq === 'once') {
    return startDate >= windowStart && startDate <= windowEnd && !exdates.has(startDate)
      ? [startDate]
      : [];
  }

  const out = [];
  // A rolling window is ~550 days; a day-by-day walk is trivially cheap and
  // leaves no room for off-by-one bugs in period arithmetic.
  for (let ms = toMs(windowStart); ms <= toMs(windowEnd); ms += DAY_MS) {
    const date = fromMs(ms);
    if (exdates.has(date)) continue;
    if (matches(date, startDate, r)) {
      out.push(date);
      if (out.length >= limit) break;
    }
  }
  return out;
}

function matches(date, startDate, r) {
  if (r.freq === 'weekly') {
    if (!r.byday.includes(weekdayCode(date))) return false;
    return weekIndex(date, startDate) % r.interval === 0;
  }

  if (r.freq === 'monthly') {
    if (monthIndex(date, startDate) % r.interval !== 0) return false;
    if (r.bymonthday !== null) return dayOfMonth(date) === r.bymonthday;
    if (weekdayCode(date) !== r.byday[0]) return false;
    return setPosOf(date).includes(r.bysetpos);
  }

  return false;
}

// Which occurrence of this weekday within its month. A date can satisfy two
// positions at once: in most months the 4th Friday *is* the last Friday, so
// it answers to both 4 and -1.
//
// This returned a single value, collapsing to -1 whenever the date was last.
// "4th Friday of the month" therefore matched only in the roughly three
// months a year with five Fridays — a venue picking it got about one event
// per year instead of twelve, with nothing to indicate why.
function setPosOf(date) {
  const dom = dayOfMonth(date);
  const nth = Math.floor((dom - 1) / 7) + 1;
  const isLast = dom + 7 > daysInMonth(date);
  return isLast ? [nth, -1] : [nth];
}

// ------------------------------------------------------------------ export

// RFC 5545 RRULE for the ICS feed. Built by concatenation on purpose: this is
// the only place iCalendar syntax exists, and it is write-only.
export function toRRule(rule) {
  const r = normaliseRule(rule);
  if (r.freq === 'once') return null;

  const parts = [];
  parts.push(`FREQ=${r.freq === 'weekly' ? 'WEEKLY' : 'MONTHLY'}`);
  if (r.interval > 1) parts.push(`INTERVAL=${r.interval}`);
  if (r.byday) parts.push(`BYDAY=${r.byday.join(',')}`);
  if (r.bymonthday !== null) parts.push(`BYMONTHDAY=${r.bymonthday}`);
  if (r.bysetpos !== null) parts.push(`BYSETPOS=${r.bysetpos}`);
  if (r.until) parts.push(`UNTIL=${r.until.replaceAll('-', '')}T235959Z`);
  return parts.join(';');
}

// ----------------------------------------------------------------- helpers

function normaliseByday(byday) {
  const list = Array.isArray(byday) ? byday : byday ? [byday] : [];
  const out = [];
  for (const raw of list) {
    const code = String(raw).toUpperCase();
    if (!WEEKDAYS.includes(code)) throw new RecurrenceError(`unknown weekday: ${raw}`);
    if (!out.includes(code)) out.push(code);
  }
  return out.sort((a, b) => WEEKDAYS.indexOf(a) - WEEKDAYS.indexOf(b));
}

function clampInterval(value) {
  const n = value === undefined || value === null ? 1 : Number(value);
  if (!Number.isInteger(n) || n < 1 || n > 52) {
    throw new RecurrenceError(`interval out of range: ${value}`);
  }
  return n;
}

export function isDate(value) {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(toMs(value));
}

function toMs(date) {
  const [y, m, d] = date.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

function fromMs(ms) {
  return new Date(ms).toISOString().slice(0, 10);
}

function weekdayCode(date) {
  return WEEKDAYS[new Date(toMs(date)).getUTCDay()];
}

function dayOfMonth(date) {
  return Number(date.slice(8, 10));
}

function daysInMonth(date) {
  const [y, m] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

// Whole weeks between the Mondays of the two dates.
function weekIndex(date, startDate) {
  return Math.round((toMs(mondayOf(date)) - toMs(mondayOf(startDate))) / (7 * DAY_MS));
}

function mondayOf(date) {
  const dow = new Date(toMs(date)).getUTCDay(); // 0 = Sunday
  const back = (dow + 6) % 7;
  return fromMs(toMs(date) - back * DAY_MS);
}

function monthIndex(date, startDate) {
  const [y1, m1] = date.split('-').map(Number);
  const [y0, m0] = startDate.split('-').map(Number);
  return (y1 - y0) * 12 + (m1 - m0);
}

function maxDate(a, b) {
  return a > b ? a : b;
}

function minDate(a, b) {
  return a < b ? a : b;
}
